import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { PaymentProviderRow, PaymentsAdminService } from '../../features/payments/payments-admin.service';

interface ProviderOption {
  key: string;
  label: string;
  isEnabled: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-payment-provider-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaymentProviderSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="key"
      [placeholder]="t(placeholderKey)"
      [showClear]="showClear"
      [filter]="showSearch"
      filterBy="label,key"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`
    .selector { width: 100%; }
  `]
})
export class PaymentProviderSelectorComponent implements ControlValueAccessor {
  private readonly api = inject(PaymentsAdminService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'payments.selectors.providerPlaceholder';
  @Input() enabledOnly = false;
  @Input() showSearch = true;
  @Input() showClear = true;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly loading = signal(false);
  readonly options = signal<ProviderOption[]>([]);
  readonly value = signal<string | null>(null);

  private readonly allOptions = signal<ProviderOption[]>([]);
  disabled = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(next: string | null): void {
    this.value.set(next);
    this.onChange(next);
    this.onTouched();
  }

  private load(): void {
    this.loading.set(true);
    this.api.listProviders().subscribe({
      next: rows => {
        this.allOptions.set(rows.map(row => this.toOption(row)));
        this.applyFilter();
        this.loading.set(false);
      },
      error: () => {
        this.allOptions.set([]);
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private applyFilter(): void {
    const rows = this.enabledOnly
      ? this.allOptions().filter(item => item.isEnabled)
      : this.allOptions();
    this.options.set(rows);
  }

  private toOption(row: PaymentProviderRow): ProviderOption {
    return {
      key: row.providerKey,
      label: `${row.displayName} (${row.providerKey})`,
      isEnabled: row.isEnabled
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
